import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CCardFooter, CTable, CTableHead, CTableBody, CTableHeaderCell, CTableRow, CTableDataCell, CFormCheck
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import { triggerAlert } from 'components/globals/AlertNotification';
import ToastNotification from 'components/globals/ToastNotification';
import TextEditor from 'components/forms/TextEditor';
import DateInput from 'components/forms/DateInput';

const EventEvaluation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    const fetchEvaluationDetails = async () => {
        let data = null; // Declare a variable to store API response

        try {
            const response = await apiClient.get(`/event-evaluations/${id}/`);
            data = response.data; // Store response data
            if (data.date === null || data.date === "") {
                data.date = new Date().toISOString().split('T')[0];
            }

            if ((data.management_date === null || data.management_date === "") && is_director) {
                data.management_date = new Date().toISOString().split('T')[0]; 
            }

            // Set effectiveness immediately after data is loaded from API
            const finalRating = calculateFinalRating([data.rating_1, data.rating_2, data.rating_3], data.applies);
            const effectiveness = calculateEffectiveness(finalRating, data.applies);

            data.effectiveness = effectiveness;

            if (data.management_final_rating === null) {
                const managementFinalRating = calculateFinalRating([data.management_rating_1, data.management_rating_2, data.management_rating_3]);
                const managementEffectiveness = calculateEffectiveness(managementFinalRating);

                data.management_final_rating = managementFinalRating;
                data.management_effectiveness = managementEffectiveness;
            }

            setFormData(data); // Update state
            if (data.management_final_rating !== null) {
                triggerAlert('Esta Formação/Evento já foi avaliada', 'error');
                navigate(`/formacoes-eventos/${data.event_id}/avaliacao`);
            }
        } catch (error) {
            triggerAlert('Avaliação Formação/Evento não encontrada!', 'error');
            navigate("/formacoes-eventos");
        }
    };

    useEffect(() => {
        fetchEvaluationDetails();
    }, [id, location.pathname]);

    const calculateFinalRating = (ratings) => {
        // Filter out null or empty values
        const validRatings = ratings.filter((r) => r !== null && r !== "");

        // If all fields are empty, return null
        if (validRatings.length < 3) {
            return null;
        }

        const sum = validRatings.reduce((acc, val) => acc + Number(val), 0);
        const finalRating = sum / validRatings.length;

        return finalRating.toFixed(2);
    };

    const calculateEffectiveness = (finalRating, applies) => {
        let effectivenessValue = null;

        if (finalRating != null) {
            if (finalRating < 2.5) {
                effectivenessValue = "Não Eficaz";
            } else {
                effectivenessValue = "Eficaz";
                if (applies != null && applies === false) {
                    effectivenessValue += "-Não aplica conhecimento na sua função";

                }
            }
        }

        return effectivenessValue;
    };

    useEffect(() => {
        if (formData) {
            const finalRating = calculateFinalRating([formData.rating_1, formData.rating_2, formData.rating_3], formData.applies);
            const effectiveness = calculateEffectiveness(finalRating);

            setFormData((prev) => ({
                ...prev,
                final_rating: finalRating,
                effectiveness: effectiveness,
            }));
        }
    }, [formData?.rating_1, formData?.rating_2, formData?.rating_3], formData?.applies);

    useEffect(() => {
        if (formData) {
            let effectiveness = formData.effectiveness;

            if (formData.academic_grade !== null && formData.academic_grade !== "") {
                if (formData.academic_grade < 10) {
                    effectiveness = "Não Eficaz";
                } else {
                    effectiveness = "Eficaz";
                }
            }

            setFormData((prev) => ({
                ...prev,
                effectiveness: effectiveness,
            }));
        }
    }, [formData?.academic_grade]);

    useEffect(() => {
        if (formData) {
            const finalRating = calculateFinalRating([formData.management_rating_1, formData.management_rating_2, formData.management_rating_3]);
            const effectiveness = calculateEffectiveness(finalRating);

            setFormData((prev) => ({
                ...prev,
                management_final_rating: finalRating,
                management_effectiveness: effectiveness,
            }));
        }
    }, [formData?.management_rating_1, formData?.management_rating_2, formData?.management_rating_3]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updatedValue;

        if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else {
            updatedValue = value || null; // Convert empty values to null
        }

        setFormData((prevFormData) => {
            let updatedForm = {
                ...prevFormData,
                [name]: updatedValue,
            };

            return updatedForm;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await apiClient.put(`/event-evaluations/${id}/`, formData);
            triggerAlert('Avaliação Formação/Evento atualizada com sucesso!', 'success');
            navigate(`/formacoes-eventos/${formData.event}/avaliacao`);
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Erro ao gravar';
            setToast({ message: { errorMessage }, type: 'error' });
            console.error('Error submitting form:', error);
        }
    };

    if (formData !== null) {
        const isDisabled = user_id !== formData.participant;

        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification message={toast?.message} type={toast?.type} visible={!!toast} />
                <CCard>
                    <CCardHeader>
                        <h3>{formData.event_designation}</h3>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <CFormInput
                                type="text"
                                label={<strong>Participante</strong>}
                                name="participant_name"
                                value={formData.participant_name}
                                readOnly
                                plainText
                                className="mb-3"
                            />

                            <DateInput
                                label={<strong>Data</strong>}
                                name="date"
                                value={formData.date ?? new Date().toISOString().split('T')[0]}
                                onChange={handleInputChange}
                            />
                            <CTable striped bordered hover>
                                <CTableHead>
                                    <CTableRow>
                                        <CTableHeaderCell className="text-center">O colaborador aplica os conhecimentos adquiridos?</CTableHeaderCell>
                                        {formData.event_type === "Académico" ? (
                                            <>
                                                <CTableHeaderCell className="text-center">Unidade Curricular</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Nota</CTableHeaderCell>
                                            </>
                                        ) : (
                                            <>
                                                <CTableHeaderCell className="text-center">Objetivos alcançados?</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Melhoria no desempenho?</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Ação eficaz?</CTableHeaderCell>
                                            </>
                                        )}
                                        <CTableHeaderCell className="text-center">Pontuação</CTableHeaderCell>
                                        <CTableHeaderCell className="text-center">Eficácia</CTableHeaderCell>
                                    </CTableRow>
                                </CTableHead>
                                <CTableBody>
                                    <CTableRow>
                                        <CTableDataCell>
                                            <CFormSelect
                                                name="applies"
                                                value={formData.applies ?? ''}
                                                onChange={handleInputChange}
                                                className="mb-3"
                                                disabled={isDisabled}
                                            >
                                                <option value="">Selecione</option>
                                                <option value="true">Sim</option>
                                                <option value="false">Não</option>
                                            </CFormSelect>
                                        </CTableDataCell>

                                        {formData.event_type === "Académico" ? (
                                            <>
                                                <CTableDataCell>
                                                    <CFormInput type="text" name="academic_course" value={formData.academic_course} onChange={handleInputChange} disabled={isDisabled} />
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormSelect name="academic_grade" value={formData.academic_grade} onChange={handleInputChange} disabled={isDisabled}>
                                                        <option value="">Selecione</option>
                                                        {[...Array(20).keys()].map((n) => (
                                                            <option key={n + 1} value={n + 1}>{n + 1}</option>
                                                        ))}
                                                    </CFormSelect>
                                                </CTableDataCell>
                                            </>
                                        ) : (
                                            ["rating_1", "rating_2", "rating_3"].map((key) => (
                                                <CTableDataCell key={key}>
                                                    <CFormSelect name={key} value={formData[key]} onChange={handleInputChange} disabled={isDisabled}>
                                                        <option value="">Selecione</option>
                                                        <option value="1">Insatisfatório</option>
                                                        <option value="2">Pouco Satisfatório</option>
                                                        <option value="3">Satisfatório</option>
                                                        <option value="4">Bom</option>
                                                        <option value="5">Excelente</option>
                                                    </CFormSelect>
                                                </CTableDataCell>
                                            ))
                                        )}

                                        <CTableDataCell>
                                            <CFormInput type="number" step={0.01} name="final_rating" value={formData.final_rating} readOnly />
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CFormInput type="text" name="effectiveness" value={formData.effectiveness} readOnly />
                                        </CTableDataCell>
                                    </CTableRow>
                                </CTableBody>
                            </CTable>

                            <TextEditor
                                label="Em caso de Ineficácia, justifique"
                                value={formData.comments ?? ''}
                                onChange={(value) =>
                                    handleInputChange({ target: { name: "comments", value } })
                                }
                                className="mb-3"
                            />

                            <br />

                            {is_director && (
                                <>
                                    <CTable striped bordered hover>
                                        <CTableHead>
                                            <CTableRow>
                                                <CTableHeaderCell colSpan={7} className="text-center">Avaliação Chefias</CTableHeaderCell>
                                            </CTableRow>

                                            <CTableRow>
                                                <CTableHeaderCell className="text-center">Data</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Os objetivos / competências previstas foram alcançadas?</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">O colaborador melhorou o nível de desempenho das suas funções pela aplicação dos novos conhecimentos?</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Na globalidade considera que a ação foi eficaz?</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Pontuação</CTableHeaderCell>
                                                <CTableHeaderCell className="text-center">Eficácia</CTableHeaderCell>
                                            </CTableRow>
                                        </CTableHead>
                                        <CTableBody>
                                            <CTableRow>
                                                <CTableDataCell className='text-center'>
                                                    <DateInput
                                                        name="management_date"
                                                        value={formData.management_date ?? new Date().toISOString().split('T')[0]}
                                                        onChange={handleInputChange}
                                                    />

                                                </CTableDataCell>
                                                {["management_rating_1", "management_rating_2", "management_rating_3"].map((key) => (
                                                    <CTableDataCell key={key}>
                                                        <CFormSelect name={key} value={formData[key]} onChange={handleInputChange}>
                                                            <option value="">Selecione</option>
                                                            <option value="1">Insatisfatório</option>
                                                            <option value="2">Pouco Satisfatório</option>
                                                            <option value="3">Satisfatório</option>
                                                            <option value="4">Bom</option>
                                                            <option value="5">Excelente</option>
                                                        </CFormSelect>
                                                    </CTableDataCell>
                                                ))}
                                                <CTableDataCell>
                                                    <CFormInput type="number" step={0.01} name="final_management_rating" value={formData.management_final_rating} readOnly />
                                                </CTableDataCell>
                                                <CTableDataCell>
                                                    <CFormInput type="text" name="management_effectiveness" value={formData.management_effectiveness} readOnly />
                                                </CTableDataCell>
                                            </CTableRow>
                                        </CTableBody>
                                    </CTable>

                                    <TextEditor
                                        label="Em caso de Ineficácia, justifique"
                                        value={formData.management_comments ?? ''}
                                        onChange={(value) =>
                                            handleInputChange({ target: { name: "management_comments", value } })
                                        }
                                        className="mb-3"
                                    />
                                </>
                            )}
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate(`/formacoes-eventos/${formData.event_id}/avaliacao`)}>
                            Cancelar
                        </CButton>
                        <CButton color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            Confirmar
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }
};

export default EventEvaluation;